import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, api, useAuth } from "@/context/AuthContext";
import { byKey, PLATFORMS } from "@/lib/platforms";
import { toast } from "sonner";
import { ArrowUpRight, Bookmark, Users } from "lucide-react";

function FeatureRows({ p, dark }) {
  return (
    <div>
      {p.features.map((f, i) => {
        const FIcon = f.icon;
        return (
        <div key={f.path} className={`feature-row py-6 grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 ${i % 3 === 1 ? "md:pl-10" : i % 3 === 2 ? "md:pl-20" : ""}`} data-testid={`feature-${p.key}-${i}`}>
          <div className="md:col-span-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl" style={{ background: dark ? "rgba(255,255,255,0.1)" : p.soft, color: dark ? "#C7CBFA" : p.deep }}>
              <FIcon size={17} />
            </span>
          </div>
          <div className="md:col-span-9">
            <h3 className="font-display text-xl sm:text-2xl">{f.name}</h3>
            <p className={`mt-1.5 text-sm max-w-xl ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{f.blurb}</p>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function FeatureColumns({ p, dark }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {p.features.map((f, i) => {
        const FIcon = f.icon;
        return (
        <div
          key={f.path}
          className={`rounded-3xl border p-6 ${dark ? "border-white/15 bg-white/5" : "border-[var(--line)] bg-white/45"} ${i % 4 === 1 ? "md:mt-8" : i % 4 === 2 ? "md:-mt-4" : ""} hover:-translate-y-1 transition-transform`}
          data-testid={`feature-${p.key}-${i}`}
        >
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl" style={{ background: dark ? "rgba(255,255,255,0.1)" : p.soft, color: dark ? "#C7CBFA" : p.deep }}>
            <FIcon size={17} />
          </span>
          <h3 className="font-display text-xl mt-3">{f.name}</h3>
          <p className={`mt-1.5 text-sm ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{f.blurb}</p>
        </div>
        );
      })}
    </div>
  );
}

function FeatureTimeline({ p, dark }) {
  return (
    <div className="relative pl-8">
      <span className="absolute left-2.5 top-2 bottom-2 w-px" style={{ background: dark ? "rgba(255,255,255,0.2)" : p.hue, opacity: dark ? 1 : 0.35 }} />
      <div className="space-y-9">
        {p.features.map((f, i) => {
          const FIcon = f.icon;
          return (
          <div key={f.path} className="relative" data-testid={`feature-${p.key}-${i}`}>
            <span className="absolute -left-8 top-1.5 w-3.5 h-3.5 rounded-full border-2" style={{ background: dark ? p.deep : "var(--paper)", borderColor: dark ? "#C7CBFA" : p.hue }} />
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl" style={{ background: dark ? "rgba(255,255,255,0.1)" : p.soft, color: dark ? "#C7CBFA" : p.deep }}>
              <FIcon size={17} />
            </span>
            <h3 className="font-display text-xl sm:text-2xl mt-2">{f.name}</h3>
            <p className={`mt-1.5 text-sm max-w-xl ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{f.blurb}</p>
          </div>
          );
        })}
      </div>
    </div>
  );
}

const RENDERERS = { stagger: FeatureRows, warm: FeatureRows, statements: FeatureRows, columns: FeatureColumns, stages: FeatureColumns, night: FeatureTimeline, timeline: FeatureTimeline };

export default function PlatformPage({ platformKey }) {
  const p = byKey(platformKey);
  const { user, setEntryOpen } = useAuth();
  const [specialists, setSpecialists] = useState([]);
  const [posts, setPosts] = useState([]);
  const dark = p.dark;
  const Renderer = RENDERERS[p.signature] || FeatureRows;

  useEffect(() => {
    axios.get(`${API}/specialists`, { params: { platform: p.key } }).then((r) => setSpecialists(r.data)).catch(() => {});
    axios.get(`${API}/blog`, { params: { platform: p.key } }).then((r) => setPosts(r.data)).catch(() => {});
  }, [p.key]);

  const keep = async () => {
    if (!user) { setEntryOpen(true); return; }
    try {
      await api.post("/record", { platform: p.key, kind: "place", title: p.name, note: "Kept from the platform page" });
      toast(`${p.name} is now in your record.`);
    } catch (e) {
      toast("Could not save that. Try again.");
    }
  };

  return (
    <main className={`pt-16 ${dark ? "dark-scope text-white" : ""}`} style={{ background: dark ? p.deep : "var(--paper)" }} data-testid={`platform-page-${p.key}`}>
      {/* hero */}
      <section className="relative overflow-hidden" style={{ background: dark ? p.deep : p.soft }}>
        <div className="absolute -right-24 -top-24 w-96 h-96 blob-a drift opacity-30" style={{ background: p.hue }} />
        <div className="absolute -left-16 bottom-0 w-56 h-56 blob-b drift opacity-20" style={{ background: p.hue, animationDelay: "2s" }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-16 relative">
          <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white" style={{ background: p.hue }}>
            <p.icon size={13} /> {p.tagline}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05] max-w-3xl">{p.name}</h1>
          <p className={`mt-3 font-display text-xl sm:text-2xl ${dark ? "text-white/80" : ""}`} style={{ color: dark ? undefined : p.deep }}>{p.tagline}</p>
          <p className={`mt-5 max-w-xl ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{p.intro}</p>
          <p className={`mt-3 max-w-xl text-sm font-medium ${dark ? "text-white/85" : ""}`}>{p.core}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span className="rounded-full px-4 py-1.5 text-xs font-mono-nc text-white pulse-soft" style={{ background: p.hue }} data-testid={`platform-status-${p.key}`}>
              In build. Arriving in full.
            </span>
            <button onClick={keep} className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold border transition-transform hover:-translate-y-0.5 ${dark ? "border-white/25" : "border-[var(--line)] bg-white/50"}`} data-testid={`platform-keep-${p.key}`}>
              <Bookmark size={14} />
              Keep this place
            </button>
          </div>
        </div>
      </section>

      {/* features */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display text-lg font-semibold mb-8">What lives here</h2>
        <Renderer p={p} dark={dark} />
      </section>

      {/* specialists */}
      {specialists.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
          <h2 className="font-display text-lg font-semibold mb-6">The people answering</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {specialists.map((s, i) => (
              <div key={s.slug} className={`rounded-3xl border p-7 ${dark ? "border-white/15 bg-white/5" : "border-[var(--line)] bg-white/45"} ${i % 2 === 1 ? "md:mt-6" : ""}`} data-testid={`specialist-${s.slug}`}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 blob-c flex items-center justify-center text-white font-display font-semibold text-lg" style={{ background: p.hue }}>
                    {s.name.split(" ").map((w) => w[0]).slice(-2).join("")}
                  </div>
                  <div>
                    <p className="font-semibold">{s.name}</p>
                    <p className={`text-sm ${dark ? "text-white/60" : "text-[var(--ink-soft)]"}`}>{s.role} · {s.years} years</p>
                  </div>
                </div>
                <p className="font-display text-lg mt-4 leading-snug">"{s.philosophy}"</p>
                <p className={`text-sm mt-3 ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{s.bio}</p>
                <p className={`text-xs mt-4 font-mono-nc ${dark ? "text-white/50" : "text-[var(--ink-soft)]"}`}>Speaks {s.languages.join(", ")} · One to one sessions coming soon</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* community */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        <div className={`rounded-3xl p-8 sm:p-10 text-white grain relative overflow-hidden`} style={{ background: dark ? "rgba(255,255,255,0.07)" : p.hue }}>
          <div className="flex items-start gap-4 relative">
            <Users size={22} className="mt-1 shrink-0" />
            <div>
              <h2 className="font-display text-2xl">The room</h2>
              <p className="mt-2 max-w-xl opacity-90 text-sm sm:text-base">{p.room}</p>
              <p className="mt-3 text-xs font-mono-nc opacity-75">Opens with the full platform. Your alias already works here.</p>
            </div>
          </div>
        </div>
      </section>

      {/* blog */}
      {posts.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
          <h2 className="font-display text-lg font-semibold mb-6">From the blog</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {posts.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className={`rounded-3xl border p-7 transition-all hover:-translate-y-1 ${dark ? "border-white/15 bg-white/5" : "border-[var(--line)] bg-white/45"}`} data-testid={`platform-post-${post.slug}`}>
                <h3 className="font-display text-xl leading-snug">{post.title}</h3>
                <p className={`text-sm mt-2 ${dark ? "text-white/70" : "text-[var(--ink-soft)]"}`}>{post.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold" style={{ color: dark ? "#B9B4F5" : p.hue }}>
                  Read it <ArrowUpRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* other doors */}
      <section className={`border-t ${dark ? "border-white/15" : "border-[var(--line)]"}`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
          <p className={`kicker mb-4 ${dark ? "text-white/50" : "text-[var(--ink-soft)]"}`}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== p.key).map((x) => (
              <Link key={x.key} to={x.path} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5 ${dark ? "border-white/20" : "border-[var(--line)] bg-white/40"}`} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />
                {x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
